import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  apiDelete,
  apiGet,
  apiPost,
  apiPut,
  type VendorPage,
  type VendorPageUpsertRequest,
} from "@/lib/api";

const VENDOR_PAGES_KEY = ["vendor-pages"] as const;
const STATIC_CATALOG_TIME = Number.POSITIVE_INFINITY;

export function useVendorPages(params?: {
  vendor?: string;
  subscription?: string;
  section?: string;
  page_type?: string;
  capability?: string;
  status?: string;
}) {
  return useQuery<VendorPage[]>({
    queryKey: [...VENDOR_PAGES_KEY, params] as const,
    queryFn: () => apiGet<VendorPage[]>("/api/v1/vendor-pages", params),
    gcTime: STATIC_CATALOG_TIME,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: STATIC_CATALOG_TIME,
  });
}

export function useCreateVendorPage() {
  const qc = useQueryClient();
  return useMutation<VendorPage, Error, VendorPageUpsertRequest>({
    mutationFn: (body) => apiPost<VendorPage>("/api/v1/vendor-pages", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: VENDOR_PAGES_KEY });
    },
  });
}

export function useUpdateVendorPage() {
  const qc = useQueryClient();
  return useMutation<
    VendorPage,
    Error,
    { id: string; body: VendorPageUpsertRequest }
  >({
    mutationFn: ({ id, body }) => apiPut<VendorPage>(`/api/v1/vendor-pages/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: VENDOR_PAGES_KEY });
    },
  });
}

export function useDeleteVendorPage() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiDelete(`/api/v1/vendor-pages/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: VENDOR_PAGES_KEY });
    },
  });
}
